import { useEffect, useState } from "react";
import { useRevalidator } from "@remix-run/react";
import { Timer } from "phosphor-react";
import { cn } from "~/lib/utils";

interface UnlimitedMessagesPillProps {
  /** ISO timestamp of when the active window ends. Null/undefined = no active window. */
  unlimitedMessagesUntil: string | null | undefined;
  onClick?: () => void;
  compact?: boolean;
}

/**
 * Header pill that ticks down the active "unlimited messages" window. Renders
 * nothing when there's no active window. Auto-triggers a loader revalidation
 * the moment the window expires so the rest of the UI (credits display, send
 * bar) snaps back to normal pricing without a manual refresh.
 */
export default function UnlimitedMessagesPill({
  unlimitedMessagesUntil,
  onClick,
  compact = false,
}: UnlimitedMessagesPillProps) {
  const revalidator = useRevalidator();
  const endsAtMs = unlimitedMessagesUntil
    ? new Date(unlimitedMessagesUntil).getTime()
    : 0;

  // Skip the first SSR pass entirely — the countdown text changes every
  // second so the server-rendered HTML inevitably mismatches the client's
  // clock and React throws a hydration error. Mount on the client first,
  // then render the live countdown.
  const [mounted, setMounted] = useState(false);
  const [now, setNow] = useState(0);
  useEffect(() => {
    setMounted(true);
    setNow(Date.now());
  }, []);

  useEffect(() => {
    if (!mounted || !endsAtMs || endsAtMs <= Date.now()) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [mounted, endsAtMs]);

  // When the timestamp becomes stale (passed expiry without a server-side
  // revalidation event) trigger one ourselves so the loader returns null.
  useEffect(() => {
    if (!mounted || !endsAtMs) return;
    if (now >= endsAtMs) {
      revalidator.revalidate();
    }
  }, [mounted, now, endsAtMs, revalidator]);

  if (!mounted || !endsAtMs) return null;
  const remainingMs = endsAtMs - now;
  if (remainingMs <= 0) return null;

  const totalSec = Math.ceil(remainingMs / 1000);
  const minutes = Math.floor(totalSec / 60);
  const seconds = totalSec % 60;
  const label = `${minutes}:${seconds.toString().padStart(2, "0")}`;
  const expiringSoon = remainingMs <= 60_000;

  return (
    <button
      type="button"
      onClick={onClick}
      title="Unlimited messages active"
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full font-semibold transition-transform active:scale-95",
        "bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-[0_0_10px_rgba(16,185,129,0.45)] hover:shadow-[0_0_14px_rgba(16,185,129,0.6)]",
        compact ? "px-2 py-1 text-[11px]" : "px-3 py-1.5 text-xs",
        // animate-pulse is opacity-only (1 → 0.5 → 1) so the pill blinks in
        // place rather than scaling. animate-pulsesubtle would change scale,
        // which felt too bouncy for an urgency cue.
        expiringSoon && "animate-pulse from-orange-500 to-rose-500 shadow-[0_0_10px_rgba(249,115,22,0.55)]",
      )}
    >
      <Timer size={compact ? 14 : 16} weight="bold" />
      <span className="tabular-nums">{label}</span>
    </button>
  );
}
